import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, MailWarning, ArrowRight, Loader2, CheckCircle2, Inbox } from "lucide-react";
import { api, API } from "@/lib/api";

const STORAGE_KEY = "cnc_download_token";

export const EmailGate = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState(() => (localStorage.getItem(STORAGE_KEY) ? "unlocked" : "idle"));
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [devLink, setDevLink] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [build, setBuild] = useState(null);

  useEffect(() => {
    if (state !== "unlocked") return;
    api.get("/build-info").then(({ data }) => setBuild(data)).catch(() => {});
  }, [state]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    api
      .get("/session", { params: { token: stored } })
      .then(({ data }) => {
        if (!data.valid || !data.verified) {
          localStorage.removeItem(STORAGE_KEY);
          setToken("");
          setState("idle");
          setNotice("Your previous confirmation is no longer valid — re-enter your email to unlock again.");
        }
      })
      .catch(() => {});
  }, []);

  const unlock = (tok) => {
    localStorage.setItem(STORAGE_KEY, tok);
    setToken(tok);
    setState("unlocked");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setState("pending");
    try {
      const { data } = await api.post("/signup", { email });
      if (data.status === "already_verified") {
        unlock(data.token);
      } else {
        setDevLink(data.dev_verification_url || "");
        setState("sent");
      }
    } catch (err) {
      setState("idle");
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  return (
    <div data-testid="email-gate" className="w-full max-w-xl">
      <AnimatePresence mode="wait">
        {state === "unlocked" ? (
          <motion.div
            key="unlocked"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-[#00FF41]/40 bg-[#00FF41]/5 p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-4 h-4 text-[#00FF41]" />
              <span className="label-mono !text-[#00FF41]">Email confirmed — build unlocked</span>
            </div>
            <a
              data-testid="download-button"
              href={`${API}/download?token=${token}`}
              className="btn-forge w-full sm:w-auto"
            >
              <Download className="w-4 h-4" /> Download CNCroutersoft 1.0.0
            </a>
            <p className="mt-3 font-mono2 text-[11px] text-white/40" data-testid="build-info-line">
              {build
                ? `${build.filename} · ${(build.size / 1048576).toFixed(1)} MB · uploaded ${build.updated_at.slice(0, 16).replace("T", " ")} UTC · Windows 10/11 64-bit`
                : "Windows 10/11 64-bit"}
            </p>
          </motion.div>
        ) : state === "sent" ? (
          <motion.div
            key="sent"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/15 bg-white/[0.03] p-6"
            data-testid="email-sent-state"
          >
            <div className="flex items-center gap-2 mb-3">
              <Inbox className="w-4 h-4 text-[#FF3B00]" />
              <span className="label-mono !text-[#FF3B00]">Check your inbox</span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              We sent a confirmation link to <span className="text-white font-medium">{email}</span>.
              Click it to unlock the download.
            </p>
            {devLink && (
              <div className="mt-4 border border-dashed border-[#F5A623]/50 bg-[#F5A623]/5 p-4" data-testid="mock-inbox">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#F5A623] mb-2 flex items-center gap-2">
                  <MailWarning className="w-3.5 h-3.5" /> Mocked inbox — email sending is disabled in this demo
                </p>
                <a
                  data-testid="mock-verification-link"
                  href={devLink}
                  className="font-mono2 text-xs text-white/80 break-all underline decoration-[#FF3B00] underline-offset-4 hover:text-[#FF3B00] transition-colors"
                >
                  {devLink}
                </a>
              </div>
            )}
            <button
              data-testid="gate-back-button"
              onClick={() => setState("idle")}
              className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors"
            >
              ← Use a different email
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            exit={{ opacity: 0, y: -12 }}
            onSubmit={submit}
            className="border border-white/15 bg-black/50 backdrop-blur-md p-6"
            data-testid="email-form"
          >
            <label htmlFor="gate-email" className="label-mono block mb-3">
              Free for personal use — enter your email, it goes straight onto the list
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                id="gate-email"
                data-testid="email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@workshop.com"
                className="flex-1 bg-transparent border-b border-white/25 focus:border-[#FF3B00] outline-none py-3 font-mono2 text-sm text-white placeholder:text-white/25 transition-colors"
              />
              <button
                data-testid="email-submit-button"
                type="submit"
                disabled={state === "pending"}
                className="btn-forge"
              >
                {state === "pending" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Unlock download <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
            {error && (
              <p data-testid="email-error" className="mt-3 font-mono2 text-xs text-[#FF3B00]">{error}</p>
            )}
            {notice && (
              <p data-testid="session-notice" className="mt-3 font-mono2 text-xs text-[#F5A623]">{notice}</p>
            )}
            <p className="mt-4 font-mono2 text-[11px] text-white/35 leading-relaxed">
              One confirmation email. Occasional update notices. No spam, ever.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
